import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-hot-toast";
import { fetchStreamingUrl, getTrackDetail } from "../services/soundcloud/api";
import { normalizePlayableQueue, normalizePlayableTrack } from "../services/player/trackAdapter";

const PlayerContext = createContext(null);

function formatTime(seconds) {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(safe / 60);
  const remaining = safe % 60;
  return `${minutes}:${String(remaining).padStart(2, "0")}`;
}

function getNextPlayableIndex(queue = [], startIndex = 0, options = {}) {
  const total = queue.length;
  if (!total) return -1;

  const wrap = options.wrap !== false;
  const direction = options.direction === "backward" ? -1 : 1;
  const boundedStart = Math.min(Math.max(Number(startIndex) || 0, 0), total - 1);

  if (!queue[boundedStart]?.isUnavailable) return boundedStart;

  for (let step = 1; step <= total; step += 1) {
    let index = boundedStart + step * direction;
    if (wrap) {
      index = ((index % total) + total) % total;
    }

    if (index < 0 || index >= total) continue;
    if (!queue[index]?.isUnavailable) return index;
  }

  return -1;
}

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const indexRef = useRef(0);
  const pendingPlayIdRef = useRef(0);
  const playAtIndexRef = useRef(() => {});
  const shuffleRef = useRef(false);
  const repeatModeRef = useRef("off");

  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [repeatMode, setRepeatMode] = useState("off");
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [sleepTimerEndsAt, setSleepTimerEndsAt] = useState(null);
  const [sleepTimerRemainingMs, setSleepTimerRemainingMs] = useState(0);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "none";
    audio.volume = 1;
    audioRef.current = audio;

    const onPlaying = () => {
      setIsPlaying(true);
      setIsBuffering(false);
    };

    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onLoadedMetadata = () => setDuration(Number(audio.duration) || 0);
    const onTimeUpdate = () => setCurrentTime(Number(audio.currentTime) || 0);
    const onEnded = () => {
      if (repeatModeRef.current === "one") {
        audio.currentTime = 0;
        audio.play().catch(() => {});
        return;
      }

      const currentQueue = queueRef.current;
      if (!currentQueue.length) return;

      if (shuffleRef.current && currentQueue.length > 1) {
        const current = indexRef.current;
        let next = current;
        while (next === current) {
          next = Math.floor(Math.random() * currentQueue.length);
        }
        playAtIndexRef.current(next, { showToast: false });
        return;
      }

      const isLast = indexRef.current >= currentQueue.length - 1;
      if (isLast) {
        if (repeatModeRef.current === "all") {
          playAtIndexRef.current(0, { showToast: false });
        } else {
          setIsPlaying(false);
        }
        return;
      }

      playAtIndexRef.current(indexRef.current + 1, { showToast: false });
    };

    const onError = () => {
      setIsBuffering(false);
      setIsPlaying(false);
      toast.error("Unable to play this track.");
    };

    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.pause();
      audio.src = "";
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, []);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    indexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    shuffleRef.current = shuffleEnabled;
  }, [shuffleEnabled]);

  useEffect(() => {
    repeatModeRef.current = repeatMode;
  }, [repeatMode]);

  useEffect(() => {
    if (!sleepTimerEndsAt) {
      setSleepTimerRemainingMs(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, sleepTimerEndsAt - Date.now());
      setSleepTimerRemainingMs(remaining);

      if (remaining > 0) return;

      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
      }
      setIsPlaying(false);
      setSleepTimerEndsAt(null);
      setSleepTimerRemainingMs(0);
      toast("Sleep timer ended. Playback paused.", { icon: "😴" });
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [sleepTimerEndsAt]);

  const ensurePlayableTrackDetail = useCallback(async (track) => {
    const rawTrack = track.rawTrack || {};
    const hasStreamMetadata = Boolean(rawTrack?.track_authorization) &&
      Array.isArray(rawTrack?.media?.transcodings) &&
      rawTrack.media.transcodings.length > 0;

    if (hasStreamMetadata) return rawTrack;

    const detail = await getTrackDetail(track.id);
    return detail;
  }, []);

  const resolvePlayableUrl = useCallback(async (trackPayload) => {
    try {
      const progressive = await fetchStreamingUrl(trackPayload, { protocol: "progressive" });
      if (progressive?.url) return progressive.url;
    } catch {
      // fallback below
    }

    const hls = await fetchStreamingUrl(trackPayload, { protocol: "hls" });
    if (hls?.url) return hls.url;
    throw new Error("No streaming URL available.");
  }, []);

  const playAtIndex = useCallback(async (nextIndex, options = {}) => {
    const currentQueue = queueRef.current;
    const resolvedIndex = getNextPlayableIndex(currentQueue, nextIndex, {
      wrap: options.wrapUnavailable !== false,
      direction: options.direction === "backward" ? "backward" : "forward",
    });

    if (resolvedIndex < 0) {
      toast.error("All tracks in queue are unavailable in your country.");
      setIsPlaying(false);
      setIsBuffering(false);
      return;
    }

    const nextTrack = currentQueue[resolvedIndex];
    const audio = audioRef.current;
    if (!audio || !nextTrack) return;

    const token = pendingPlayIdRef.current + 1;
    pendingPlayIdRef.current = token;
    setCurrentIndex(resolvedIndex);
    setIsBuffering(true);

    if (resolvedIndex !== nextIndex && options.silentSkipNotice !== true) {
      toast("Skipped unavailable tracks", { icon: "⏭️" });
    }

    try {
      const fullTrack = await ensurePlayableTrackDetail(nextTrack);
      const streamUrl = await resolvePlayableUrl(fullTrack);

      if (pendingPlayIdRef.current !== token) return;

      audio.src = streamUrl;
      audio.currentTime = 0;
      setCurrentTime(0);

      await audio.play();
      setIsPlaying(true);

      if (options.showToast !== false) {
        toast.success(`Now playing: ${nextTrack.title}`);
      }
    } catch (error) {
      if (pendingPlayIdRef.current !== token) return;
      setIsPlaying(false);
      setIsBuffering(false);
      toast.error(error?.message || "Unable to start playback.");
    }
  }, [ensurePlayableTrackDetail, resolvePlayableUrl]);

  useEffect(() => {
    playAtIndexRef.current = playAtIndex;
  }, [playAtIndex]);

  const playTracks = useCallback((items = [], startIndex = 0) => {
    const normalized = normalizePlayableQueue(items);
    if (!normalized.length) {
      toast.error("No playable tracks found.");
      return;
    }

    const boundedStart = Math.min(Math.max(startIndex, 0), normalized.length - 1);
    setQueue(normalized);
    queueRef.current = normalized;
    setCurrentIndex(boundedStart);
    indexRef.current = boundedStart;
    playAtIndex(boundedStart);
  }, [playAtIndex]);

  const playTrack = useCallback((track, queueSource = null, queueIndex = 0) => {
    if (Array.isArray(queueSource) && queueSource.length > 0) {
      playTracks(queueSource, queueIndex);
      return;
    }

    const normalizedTrack = normalizePlayableTrack(track);
    if (!normalizedTrack.id) {
      toast.error("Track data is invalid.");
      return;
    }

    playTracks([track], 0);
  }, [playTracks]);

  const togglePlayPause = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!queueRef.current.length) {
      toast("Pick a track to start playback", { icon: "🎵" });
      return;
    }

    if (audio.paused) {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {
        toast.error("Unable to resume playback.");
      }
      return;
    }

    audio.pause();
    setIsPlaying(false);
  }, []);

  const playNext = useCallback(() => {
    const currentQueue = queueRef.current;
    if (!currentQueue.length) return;

    if (shuffleEnabled && currentQueue.length > 1) {
      const current = indexRef.current;
      let next = current;
      while (next === current) {
        next = Math.floor(Math.random() * currentQueue.length);
      }
      playAtIndex(next, { direction: "forward" });
      return;
    }

    const next = indexRef.current + 1;
    if (next < currentQueue.length) {
      playAtIndex(next, { direction: "forward" });
      return;
    }

    if (repeatMode === "all") {
      playAtIndex(0, { direction: "forward" });
    }
  }, [playAtIndex, repeatMode, shuffleEnabled]);

  const playPrevious = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !queueRef.current.length) return;

    if (audio.currentTime > 5) {
      audio.currentTime = 0;
      return;
    }

    const prev = indexRef.current - 1;
    if (prev >= 0) {
      playAtIndex(prev, { direction: "backward" });
      return;
    }

    if (repeatMode === "all") {
      playAtIndex(queueRef.current.length - 1, { direction: "backward" });
    }
  }, [playAtIndex, repeatMode]);

  const playQueueIndex = useCallback((index) => {
    const nextIndex = Number(index);
    if (!Number.isInteger(nextIndex)) return;
    if (nextIndex < 0 || nextIndex >= queueRef.current.length) return;
    playAtIndex(nextIndex);
  }, [playAtIndex]);

  const clearQueue = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = "";
      audio.currentTime = 0;
    }

    setQueue([]);
    queueRef.current = [];
    setCurrentIndex(0);
    indexRef.current = 0;
    setIsPlaying(false);
    setIsBuffering(false);
    setDuration(0);
    setCurrentTime(0);
  }, []);

  const removeQueueIndex = useCallback((index) => {
    const nextIndex = Number(index);
    if (!Number.isInteger(nextIndex)) return;

    const currentQueue = queueRef.current || [];
    if (nextIndex < 0 || nextIndex >= currentQueue.length) return;

    if (currentQueue.length === 1) {
      clearQueue();
      return;
    }

    const updatedQueue = currentQueue.filter((_, itemIndex) => itemIndex !== nextIndex);
    setQueue(updatedQueue);
    queueRef.current = updatedQueue;

    if (nextIndex < indexRef.current) {
      const shifted = indexRef.current - 1;
      setCurrentIndex(shifted);
      indexRef.current = shifted;
      return;
    }

    if (nextIndex > indexRef.current) {
      return;
    }

    const fallbackIndex = Math.min(indexRef.current, updatedQueue.length - 1);
    setCurrentIndex(fallbackIndex);
    indexRef.current = fallbackIndex;
    playAtIndexRef.current(fallbackIndex, { showToast: false });
  }, [clearQueue]);

  const moveQueueIndex = useCallback((fromIndex, toIndex) => {
    const from = Number(fromIndex);
    const to = Number(toIndex);
    if (!Number.isInteger(from) || !Number.isInteger(to)) return;

    const currentQueue = [...(queueRef.current || [])];
    const total = currentQueue.length;
    if (!total) return;
    if (from < 0 || from >= total || to < 0 || to >= total) return;
    if (from === to) return;

    const [moved] = currentQueue.splice(from, 1);
    currentQueue.splice(to, 0, moved);

    setQueue(currentQueue);
    queueRef.current = currentQueue;

    if (indexRef.current === from) {
      setCurrentIndex(to);
      indexRef.current = to;
      return;
    }

    if (indexRef.current === to) {
      const replacement = from > to ? to + 1 : to - 1;
      setCurrentIndex(replacement);
      indexRef.current = replacement;
    }
  }, []);

  const enqueueAfterCurrent = useCallback((items = [], options = {}) => {
    const normalized = normalizePlayableQueue(items);
    if (!normalized.length) return 0;

    const dedupe = options.dedupe !== false;
    const currentQueue = queueRef.current || [];
    const currentIds = new Set(currentQueue.map((item) => item.id));
    const nextItems = [];
    const seenIncoming = new Set();

    normalized.forEach((item) => {
      if (!item?.id) return;
      if (seenIncoming.has(item.id)) return;
      seenIncoming.add(item.id);

      if (dedupe && currentIds.has(item.id)) return;
      nextItems.push(item);
    });

    if (!nextItems.length) return 0;

    if (!currentQueue.length) {
      setQueue(nextItems);
      queueRef.current = nextItems;
      setCurrentIndex(0);
      indexRef.current = 0;
      return nextItems.length;
    }

    const insertAt = Math.min(Math.max(indexRef.current + 1, 0), currentQueue.length);
    const mergedQueue = [
      ...currentQueue.slice(0, insertAt),
      ...nextItems,
      ...currentQueue.slice(insertAt),
    ];

    setQueue(mergedQueue);
    queueRef.current = mergedQueue;
    return nextItems.length;
  }, []);

  const seekTo = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(seconds)) return;
    const nextTime = Math.max(0, Math.min(seconds, Number(audio.duration) || seconds));
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  }, []);

  const setVolumeLevel = useCallback((nextVolume) => {
    const audio = audioRef.current;
    if (!audio) return;
    const normalized = Math.max(0, Math.min(1, Number(nextVolume) || 0));
    audio.volume = normalized;
    setVolume(normalized);
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.volume > 0) {
      audio.volume = 0;
      setVolume(0);
    } else {
      audio.volume = 0.8;
      setVolume(0.8);
    }
  }, []);

  const toggleShuffle = useCallback(() => {
    setShuffleEnabled((value) => !value);
  }, []);

  const cycleRepeatMode = useCallback(() => {
    setRepeatMode((mode) => {
      if (mode === "off") return "all";
      if (mode === "all") return "one";
      return "off";
    });
  }, []);

  const setSleepTimer = useCallback((minutes) => {
    const parsed = Number(minutes);
    if (!Number.isFinite(parsed) || parsed <= 0) return;

    const nextEndsAt = Date.now() + parsed * 60_000;
    setSleepTimerEndsAt(nextEndsAt);
    setSleepTimerRemainingMs(parsed * 60_000);
    toast.success(`Sleep timer set for ${Math.round(parsed)} minutes.`);
  }, []);

  const clearSleepTimer = useCallback((options = {}) => {
    const hasTimer = Boolean(sleepTimerEndsAt);
    setSleepTimerEndsAt(null);
    setSleepTimerRemainingMs(0);

    if (options.notify && hasTimer) {
      toast("Sleep timer turned off.", { icon: "🌙" });
    }
  }, [sleepTimerEndsAt]);

  const currentTrack = queue[currentIndex] || null;
  const progressPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const value = useMemo(() => ({
    queue,
    currentIndex,
    currentTrack,
    isPlaying,
    isBuffering,
    shuffleEnabled,
    repeatMode,
    volume,
    duration,
    currentTime,
    progressPercent,
    currentTimeLabel: formatTime(currentTime),
    durationLabel: formatTime(duration),
    playTrack,
    playTracks,
    togglePlayPause,
    playNext,
    playPrevious,
    playQueueIndex,
    removeQueueIndex,
    moveQueueIndex,
    clearQueue,
    enqueueAfterCurrent,
    seekTo,
    setVolumeLevel,
    toggleMute,
    toggleShuffle,
    cycleRepeatMode,
    setSleepTimer,
    clearSleepTimer,
    sleepTimerEndsAt,
    sleepTimerRemainingMs,
  }), [
    queue,
    currentIndex,
    currentTrack,
    isPlaying,
    isBuffering,
    shuffleEnabled,
    repeatMode,
    volume,
    duration,
    currentTime,
    progressPercent,
    playTrack,
    playTracks,
    togglePlayPause,
    playNext,
    playPrevious,
    playQueueIndex,
    removeQueueIndex,
    moveQueueIndex,
    clearQueue,
    enqueueAfterCurrent,
    seekTo,
    setVolumeLevel,
    toggleMute,
    toggleShuffle,
    cycleRepeatMode,
    setSleepTimer,
    clearSleepTimer,
    sleepTimerEndsAt,
    sleepTimerRemainingMs,
  ]);

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error("usePlayer must be used within PlayerProvider.");
  }
  return context;
}
